import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  TextField,
  Grid,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useCampaign from "hooks/useCampaign";
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/jpg"];
const CampaignForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const campaignId = queryParams.get("id");
  const { createCampaign, getCampaignById, updateCampaign, loading } = useCampaign();
  const [formData, setFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    description: "",
    rewardType: "VALUE", // Default reward type
    rewards: [{ position: "", value: "", description: "" }], // Updated rewards structure
    image: null,
    slug: "",
    featured: false, // Added featured field
  });
  const [imageError, setImageError] = useState("");

  const validateImage = (file) => {
    if (!file) return false;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setImageError("Only JPG, JPEG, and PNG formats are allowed.");
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setImageError("File size should be under 5MB.");
      return false;
    }
    return true;
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "image") {
      const file = e.target.files[0];
      if (!validateImage(file)) {
        return; // Stop execution if validation fails
      }
      setImageError("");
      setFormData((prevState) => ({
        ...prevState,
        [name]: file,
      }));
    } else if (type === "checkbox") {
      // Handle checkbox input specifically
      setFormData((prevState) => ({
        ...prevState,
        [name]: checked,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleRewardChange = (index, e) => {
    const { name, value } = e.target;
    const updatedRewards = [...formData.rewards];
    updatedRewards[index][name] = value;
    setFormData((prevState) => ({
      ...prevState,
      rewards: updatedRewards,
    }));
  };

  const handleAddReward = () => {
    // Get the last reward in the array
    const lastReward = formData.rewards[formData.rewards.length - 1];

    // Check if any of the fields in the last reward are empty
    if (!lastReward.position.trim() || !lastReward.value.trim() || !lastReward.description.trim()) {
      alert("Please fill out all fields in the current reward before adding a new one.");
      return; // Stop execution if validation fails
    }
    setFormData((prevState) => ({
      ...prevState,
      rewards: [...prevState.rewards, { position: "", value: "", description: "" }],
    }));
  };

  const handleRemoveReward = (index) => {
    // Check if there is only one reward left
    if (formData.rewards.length === 1) {
      alert("At least one reward is required. You cannot remove the last reward.");
      return; // Stop execution if validation fails
    }

    // If validation passes, remove the reward
    const updatedRewards = formData.rewards.filter((_, i) => i !== index);
    setFormData((prevState) => ({
      ...prevState,
      rewards: updatedRewards,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.startDate || !formData.endDate) {
      alert("All required fields must be filled out.");
      return;
    }

    const formDataToSubmit = new FormData();
    formDataToSubmit.append("name", formData.name);
    formDataToSubmit.append("startDate", formData.startDate);
    formDataToSubmit.append("endDate", formData.endDate);
    formDataToSubmit.append("description", formData.description);
    formDataToSubmit.append("rewardType", formData.rewardType);
    formDataToSubmit.append("slug", formData.slug);
    formDataToSubmit.append("featured", formData.featured);

    formData.rewards.forEach((reward, index) => {
      formDataToSubmit.append(`rewards[${index}][position]`, reward.position);
      formDataToSubmit.append(`rewards[${index}][value]`, reward.value);
      formDataToSubmit.append(`rewards[${index}][description]`, reward.description);
    });

    if (formData.image) {
      formDataToSubmit.append("image", formData.image);
    }

    if (campaignId) {
      const updateResponse = await updateCampaign(campaignId, formDataToSubmit);
      if (updateResponse?.success) {
        navigate("/campaign-management");
      } else {
        alert(updateResponse?.message);
      }
      return;
    }

    const createResponse = await createCampaign(formDataToSubmit);
    if (createResponse?.success) {
      setFormData({
        name: "",
        startDate: "",
        endDate: "",
        description: "",
        rewardType: "VALUE",
        rewards: [{ position: "", value: "", description: "" }],
        image: null,
        slug: "",
        featured: false,
      });
      navigate("/campaign-management");
    } else {
      alert(createResponse?.message);
    }
  };

  const fetchCampaign = async () => {
    const response = await getCampaignById(campaignId);
    if (response.success) {
      setFormData({
        name: response.data.name,
        startDate: response.data.startDate ? response.data.startDate.split("T")[0] : "",
        endDate: response.data.endDate ? response.data.endDate.split("T")[0] : "",
        description: response.data.description,
        rewardType: response.data.rewardType,
        rewards: response.data.rewards,
        image: response.data.image,
        slug: response.data.slug,
        featured: response.data.featured || false,
      });
    }
  };

  useEffect(() => {
    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId]);

  return (
    <Box
      sx={{
        width: "100%",
        background: "white",
        minHeight: `calc(100vh - 120px)`,
        p: 4,
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      {/* Form Heading */}
      <MDTypography variant="h5" component="h1" align="center" gutterBottom>
        Campaign Form
      </MDTypography>
      <MDTypography variant="caption" color="text" align="center" paragraph>
        Fill out the details below to create or update a campaign.
      </MDTypography>

      {/* Form Fields */}
      <Grid container spacing={3}>
        {/* Name Field */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Name
          </MDTypography>
          <TextField
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>

        {/* Description Field */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Description
          </MDTypography>
          <TextField
            fullWidth
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>

        {/* Start Date Field */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Start Date
          </MDTypography>
          <TextField
            fullWidth
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={(e) => {
              const selectedDate = e.target.value;
              setFormData((prev) => ({
                ...prev,
                startDate: selectedDate,
                endDate: prev.endDate && selectedDate > prev.endDate ? "" : prev.endDate, // Reset endDate if it's invalid
              }));
            }}
            inputProps={{
              max: formData.endDate || undefined, // If endDate selected, startDate should be <= endDate
            }} // Disable past dates
            required
            variant="outlined"
          />
        </Grid>

        {/* End Date Field */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            End Date
          </MDTypography>
          <TextField
            fullWidth
            name="endDate"
            type="date"
            value={formData.endDate}
            onChange={(e) => {
              const selectedDate = e.target.value;
              if (selectedDate < formData.startDate) {
                alert("End date must be greater than Start date.");
                return;
              }
              setFormData((prev) => ({
                ...prev,
                endDate: selectedDate,
              }));
            }}
            inputProps={{
              min: formData.startDate || new Date().toISOString().split("T")[0], // Disable past dates & allow only after startDate
            }}
            required
            variant="outlined"
          />
        </Grid>

        {/* Reward Type Field */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Reward Type
          </MDTypography>
          <FormControl fullWidth variant="outlined">
            <Select
              sx={{ height: "45px" }}
              name="rewardType"
              value={formData.rewardType}
              onChange={handleChange}
              required
            >
              <MenuItem value="VALUE">Value</MenuItem>
              <MenuItem value="POINT">Point</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Slug Field */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Slug
          </MDTypography>
          <TextField
            fullWidth
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>

        {/* Featured Campaign Checkbox */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <input
              type="checkbox"
              id="featured"
              name="featured"
              checked={formData.featured}
              onChange={handleChange}
              style={{ marginRight: "8px" }}
            />
            <MDTypography variant="body2" component="label" htmlFor="featured">
              Featured Campaign
            </MDTypography>
          </Box>
        </Grid>

        {/* Rewards Section */}
        <Grid item xs={12} md={12}>
          <MDTypography variant="body2" gutterBottom>
            Rewards
          </MDTypography>
          {formData.rewards.map((reward, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                {/* Position Field */}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    name="position"
                    value={reward.position}
                    onChange={(e) => handleRewardChange(index, e)}
                    label="Position"
                    variant="outlined"
                  />
                </Grid>

                {/* Value Field */}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    name="value"
                    value={reward.value}
                    onChange={(e) => handleRewardChange(index, e)}
                    label={formData.rewardType === "POINT" ? "Point" : "Value"}
                    variant="outlined"
                  />
                </Grid>

                {/* Description Field */}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    name="description"
                    value={reward.description}
                    onChange={(e) => handleRewardChange(index, e)}
                    label="Description"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              <MDButton
                variant="contained"
                color="secondary"
                onClick={() => handleRemoveReward(index)}
                sx={{ mt: 2 }}
              >
                Remove Reward
              </MDButton>
            </Box>
          ))}
          <MDButton variant="contained" color="primary" onClick={handleAddReward} sx={{ mt: 2 }}>
            Add Reward
          </MDButton>
        </Grid>

        {/* Image File Upload */}
        <Grid item xs={12} md={12}>
          <MDTypography variant="body2" gutterBottom>
            Image <span style={{ fontSize: "10px" }}>(JPG, JPEG, PNG - Max 5MB)</span>
          </MDTypography>
          <input
            accept="image/*"
            type="file"
            name="image"
            onChange={handleChange}
            style={{ display: "none" }}
            id="image-file-upload"
          />
          <label htmlFor="image-file-upload">
            <MDButton
              fullWidth
              variant="contained"
              color="info"
              component="span"
              sx={{
                padding: "8px 20px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#0056b3",
                },
              }}
            >
              Upload Image
            </MDButton>
          </label>
          {imageError && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {imageError}
            </Typography>
          )}
          {/* Image Preview */}
          {formData.image && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={
                  typeof formData.image === "string"
                    ? formData.image
                    : URL.createObjectURL(formData.image)
                }
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  borderRadius: "8px",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  objectFit: "contain", // Ensures the entire image fits within the box
                }}
              />
            </Box>
          )}
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <MDBox>
            <MDButton
              type="submit"
              variant="contained"
              color="dark"
              fullWidth
              onClick={handleSubmit}
            >
              {loading ? "Submitting..." : campaignId ? "Update Campaign" : "Submit"}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CampaignForm;
